/*
 * Connect Claude (apps/api claude/): the page Claude opens to sign in, and the Settings page that shows how to add the
 * Steward to Claude. Claude works in the one location picked here, as the owner or manager who pressed Allow, with their
 * role, and never sees the brand, head office or another location (the server holds it to that, apps/api claude/).
 */
import { Link, Navigate, useRouterState } from "@tanstack/react-router";
import { CLAUDE_ROLES, type Role } from "@steward/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "../api/client";
import { useCompanies, useMe } from "../api/hooks";
import { useSession } from "../app/session";
import { FormField } from "../components/fields";
import { useToast } from "../components/overlays";
import { Banner, Button, Card, Loading, Page, ROLE_WORDS } from "../components/parts";
import { AuthFrame } from "./auth/AuthScreens";

/** Where to go back to after signing in (the sign in pages land on /, which reads this). */
const AFTER = "steward.after";

export function takeAfterSignIn(): string | null {
  try {
    const to = sessionStorage.getItem(AFTER);
    sessionStorage.removeItem(AFTER);
    return to?.startsWith("/connect/claude?") ? to : null;
  } catch {
    return null;
  }
}

function comeBackHere(searchStr: string) {
  try {
    sessionStorage.setItem(AFTER, `/connect/claude${searchStr}`);
  } catch {
    // Private windows: they sign in, then press Connect in Claude again.
  }
}

interface Ask {
  clientId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
  method: string;
}

function readAsk(searchStr: string): Ask {
  const q = new URLSearchParams(searchStr);
  return {
    clientId: q.get("client_id") ?? "",
    redirectUri: q.get("redirect_uri") ?? "",
    state: q.get("state") ?? "",
    codeChallenge: q.get("code_challenge") ?? "",
    method: q.get("code_challenge_method") ?? "",
  };
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : "That didn't work. Try again.");

export function ConnectClaude() {
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const [ask] = useState(() => readAsk(searchStr));
  const me = useMe();
  const companies = useCompanies();
  const peek = useQuery({
    queryKey: ["claude-client", ask.clientId, ask.redirectUri],
    queryFn: () =>
      api<{ name: string; redirectHost: string }>(
        "GET",
        `auth/claude/client?clientId=${encodeURIComponent(ask.clientId)}&redirectUri=${encodeURIComponent(ask.redirectUri)}`,
      ),
    enabled: Boolean(me.data && ask.clientId && ask.redirectUri),
    retry: false,
  });
  // Locations where this login is an owner or manager. Not brands or HQ, and not a franchise that keeps its names private.
  const offices = (companies.data ?? []).filter(
    (c) => c.level === "location" && !c.namesPrivate && CLAUDE_ROLES.includes((c.role ?? "crew") as Role),
  );
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (me.error instanceof ApiError) {
    comeBackHere(searchStr);
    if (me.error.code === "two_step_needed") return <Navigate to="/two-step" search={{ mode: "needed" }} />;
    if (me.error.code === "two_step_setup_needed") return <Navigate to="/two-step" search={{ mode: "setup" }} />;
    return <Navigate to="/sign-in/office" />;
  }
  if (me.isLoading || companies.isLoading || peek.isLoading) return <Loading />;

  const companyId = picked !== "" ? picked : (offices[0]?.id ?? "");
  const allow = async () => {
    setBusy(true);
    setError(null);
    try {
      const { redirect } = await api<{ redirect: string }>("POST", "auth/claude/approve", {
        clientId: ask.clientId,
        redirectUri: ask.redirectUri,
        companyId,
        codeChallenge: ask.codeChallenge,
        codeChallengeMethod: ask.method,
        ...(ask.state ? { state: ask.state } : {}),
      });
      window.location.assign(redirect);
    } catch (err) {
      setError(errText(err));
      setBusy(false);
    }
  };
  const cancel = () => {
    const back = new URL(ask.redirectUri);
    back.searchParams.set("error", "access_denied");
    if (ask.state) back.searchParams.set("state", ask.state);
    window.location.assign(back.toString());
  };

  if (!peek.data || ask.method !== "S256")
    return (
      <AuthFrame>
        <h1>Connect Claude</h1>
        <Banner type="error">
          {peek.error ? errText(peek.error) : "This link did not come from Claude. Start again in Claude."}
        </Banner>
      </AuthFrame>
    );
  if (offices.length === 0)
    return (
      <AuthFrame>
        <h1>Connect Claude</h1>
        <p className="lead">Claude is for owners and managers of a location. Your login isn't one.</p>
      </AuthFrame>
    );

  return (
    <AuthFrame>
      <h1>Let Claude help you</h1>
      <p className="lead">
        {peek.data.name} wants to work in the Steward as you, {me.data?.name}. It sees and changes only what your role can, in the
        one location you pick, and nothing outside it.
      </p>
      {offices.length > 1 ? (
        <FormField label="Location" htmlFor="claude-company">
          <select id="claude-company" className="select" value={companyId} onChange={(e) => setPicked(e.target.value)}>
            {offices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({ROLE_WORDS[c.role ?? ""] ?? c.role})
              </option>
            ))}
          </select>
        </FormField>
      ) : (
        <p>
          Location <b>{offices[0]!.name}</b>
        </p>
      )}
      <ul className="claude-rules">
        <li>It can't see the brand, head office or any other location.</li>
        <li>It can't send texts, emails, invoices or quotes, or move money. You send them.</li>
        <li>Keys, security, who gets in, and deleting stay with you.</li>
        <li>Stop it any time in Settings, Security, Signed in devices.</li>
      </ul>
      {error ? <Banner type="error">{error}</Banner> : null}
      <Button size="large" fullWidth loading={busy} onClick={() => void allow()}>
        Allow
      </Button>
      <Button type="tertiary" fullWidth onClick={cancel}>
        Cancel
      </Button>
    </AuthFrame>
  );
}

/** Settings, Claude: the address to paste into Claude, and the steps. */
export function ClaudeSettings() {
  const { company } = useSession();
  const toast = useToast();
  const address = `${window.location.origin}/mcp`;
  const copy = () =>
    void navigator.clipboard.writeText(address).then(
      () => toast("Copied"),
      () => toast("Copy it by hand", { kind: "error" }),
    );
  return (
    <Page title="Claude" subtitle="Use Claude as your helper in the Steward">
      <Card title="Add the Steward to Claude">
        <p>
          Claude can look up clients, the schedule, invoices and quotes for {company.name}, and make changes when you say so. It
          works as you, with your role, in this location only.
        </p>
        <FormField label="Connector address" htmlFor="claude-address">
          <div className="row">
            <input
              id="claude-address"
              className="input"
              style={{ flex: 1 }}
              readOnly
              value={address}
              onFocus={(e) => e.target.select()}
            />
            <Button type="secondary" icon="file" onClick={copy}>
              Copy
            </Button>
          </div>
        </FormField>
        <ol className="claude-steps">
          <li>In Claude, open Settings, then Connectors.</li>
          <li>Press Add custom connector.</li>
          <li>Name it Steward and paste the address above.</li>
          <li>Press Connect. Sign in here, pick {company.name}, and press Allow.</li>
        </ol>
      </Card>
      <Card title="What stays with you">
        <p>
          Claude can't see the brand, head office or any other location. It can't send texts, emails, invoices or quotes, move
          money, see keys, change security or who gets in, or delete anything. It writes the words and you send them. To stop it,
          end Claude in <Link to="/settings/security">Security</Link>, Signed in devices.
        </p>
      </Card>
    </Page>
  );
}
